// Lead Desk rules: what each role sees and may do, and every calculation behind
// the screens. No React and no network, so all of it is unit-tested and the three
// role views cannot drift apart: they differ only by the table at the top.
//
// RBAC later means replacing the role the page is given (today a switcher) with
// the signed-in user's role. Nothing below changes.

export type DeskRole = 'SUPER_ADMIN' | 'ADMIN' | 'TENANT_ADMIN' | 'COUNSELLOR' | 'TELECALLER';

export const isSuperAdmin = (role: DeskRole | string | null | undefined): boolean => role === 'SUPER_ADMIN';
export const isAdmin = (role: DeskRole | string | null | undefined): boolean => role === 'SUPER_ADMIN' || role === 'ADMIN' || role === 'TENANT_ADMIN';

export type DeskScreen = 'OVERVIEW' | 'HOME' | 'STUDENTS' | 'ADD' | 'USERS' | 'REPORTS' | 'WEB_LEADS' | 'FOLDERS' | 'ACCOUNT';

export type RoleProfile = {
  label: string;
  /** Screens in sidebar order. */
  screens: DeskScreen[];
  /** What the Students screen is called for this role. */
  studentsLabel: string;
  canEditStudent: boolean;
  canBlockStudent: boolean;
  canImport: boolean;
  canExport: boolean;
  /** Change who the counsellor is on a student. */
  canReassignCounsellor: boolean;
  /** Remove a student from the call queue for good. */
  canDeleteStudent: boolean;
  /** Open the Manage Users screen's create / edit / delete controls. */
  canManageUsers: boolean;
  /** The Educational Folders Vault: create folders, upload and delete files. */
  canManageFolders: boolean;
  /** Counselor and Telecaller filters on the students list (a telecaller sees only their own, so has none). */
  canFilterByPeople: boolean;
  /** Choose which of a counsellor's telecallers a student goes to. */
  canAssignTelecaller: boolean;
  canConvertWebLead: boolean;
  /** Log the outcome of a call (status, follow-up, note) from the call queue. */
  canLogCall: boolean;
  /** A final status (Enrolled, Not Enrolled...) locks the student for this role; only a counsellor or admin can reopen it. */
  lockedWhenFinal: boolean;
  /** Only the leads assigned to the person are listed. */
  scope: 'ALL' | 'OWN_COUNSELLOR' | 'OWN_TELECALLER';
};

export const ROLE_PROFILES: Record<DeskRole, RoleProfile> = {
  SUPER_ADMIN: {
    label: 'Super Admin',
    screens: ['OVERVIEW', 'HOME', 'STUDENTS', 'ADD', 'USERS', 'REPORTS', 'WEB_LEADS', 'FOLDERS', 'ACCOUNT'],
    studentsLabel: 'All Students',
    canEditStudent: true,
    canBlockStudent: true,
    canImport: true,
    canExport: true,
    canReassignCounsellor: true,
    canDeleteStudent: true,
    canManageUsers: true,
    canManageFolders: true,
    canFilterByPeople: true,
    canAssignTelecaller: true,
    canConvertWebLead: true,
    canLogCall: true,
    lockedWhenFinal: false,
    scope: 'ALL',
  },
  TENANT_ADMIN: {
    label: 'Tenant Admin',
    screens: ['OVERVIEW', 'HOME', 'STUDENTS', 'ADD', 'USERS', 'REPORTS', 'WEB_LEADS', 'FOLDERS', 'ACCOUNT'],
    studentsLabel: 'All Students',
    canEditStudent: true,
    canBlockStudent: true,
    canImport: true,
    canExport: true,
    canReassignCounsellor: true,
    canDeleteStudent: true,
    canManageUsers: true,
    canManageFolders: true,
    canFilterByPeople: true,
    canAssignTelecaller: true,
    canConvertWebLead: true,
    canLogCall: true,
    lockedWhenFinal: false,
    scope: 'ALL',
  },
  ADMIN: {
    label: 'Tenant Admin',
    screens: ['OVERVIEW', 'HOME', 'STUDENTS', 'ADD', 'USERS', 'REPORTS', 'WEB_LEADS', 'FOLDERS', 'ACCOUNT'],
    studentsLabel: 'All Students',
    canEditStudent: true,
    canBlockStudent: true,
    canImport: true,
    canExport: true,
    canReassignCounsellor: true,
    canDeleteStudent: true,
    canManageUsers: true,
    canManageFolders: true,
    canFilterByPeople: true,
    canAssignTelecaller: true,
    canConvertWebLead: true,
    canLogCall: true,
    lockedWhenFinal: false,
    scope: 'ALL',
  },
  COUNSELLOR: {
    label: 'Counsellor',
    screens: ['OVERVIEW', 'HOME', 'STUDENTS', 'ADD', 'REPORTS', 'ACCOUNT'],
    studentsLabel: 'My Students',
    canEditStudent: true,
    canBlockStudent: true,
    canImport: true,
    canExport: true,
    canReassignCounsellor: false,
    canDeleteStudent: false,
    canManageUsers: false,
    canManageFolders: false,
    canFilterByPeople: true,
    canAssignTelecaller: true,
    canConvertWebLead: false,
    canLogCall: true,
    lockedWhenFinal: false,
    scope: 'OWN_COUNSELLOR',
  },
  TELECALLER: {
    label: 'Telecaller',
    screens: ['OVERVIEW', 'HOME', 'STUDENTS', 'ADD', 'REPORTS', 'ACCOUNT'],
    studentsLabel: 'My Calls',
    canEditStudent: true,
    canBlockStudent: false,
    canImport: false,
    canExport: false,
    canReassignCounsellor: false,
    canDeleteStudent: false,
    canManageUsers: false,
    canManageFolders: false,
    canFilterByPeople: false,
    canAssignTelecaller: false,
    canConvertWebLead: false,
    canLogCall: true,
    lockedWhenFinal: true,
    scope: 'OWN_TELECALLER',
  },
};

export const SCREEN_LABELS: Record<DeskScreen, string> = {
  OVERVIEW: 'Dashboard',
  HOME: 'CMS Home',
  STUDENTS: 'All Students',
  ADD: 'Add Student',
  USERS: 'Manage Users',
  REPORTS: 'Reports Center',
  WEB_LEADS: 'Web Leads',
  FOLDERS: 'Manage Folders',
  ACCOUNT: 'My Account',
};

export const screenLabel = (role: DeskRole, screen: DeskScreen): string => {
  if (screen === 'STUDENTS') return ROLE_PROFILES[role].studentsLabel;
  if (screen === 'HOME' && role === 'TELECALLER') return 'Call Queue';

  return SCREEN_LABELS[screen];
};

// ------------------------------------------------------------------- vocabulary

export const DESK_STATUS_LABELS: Record<string, string> = {
  NEW: 'New',
  NEW_LEAD: 'New Lead',
  CONTACTED: 'Contacted',
  BROCHURE_SENT: 'Brochure Sent',
  INTERESTED: 'Interested',
  FOLLOW_UP_REQUIRED: 'Follow-up Required',
  HAD_MEETING: 'Had Meeting',
  LONG_TERM: 'Long Term',
  ADMISSION_CONFIRMED: 'Admission Confirmed',
  ENROLLED: 'Enrolled',
  NOT_ENROLLED: 'Not Enrolled',
  CLOSED: 'Closed',
};

/** Order the pipeline chart and filter lists use. */
export const DESK_STATUS_ORDER = Object.keys(DESK_STATUS_LABELS);

/** Colour (a Twenty tag colour) for each status chip. */
export const DESK_STATUS_COLORS: Record<string, string> = {
  NEW: 'sky',
  NEW_LEAD: 'sky',
  CONTACTED: 'orange',
  BROCHURE_SENT: 'purple',
  INTERESTED: 'green',
  FOLLOW_UP_REQUIRED: 'yellow',
  HAD_MEETING: 'turquoise',
  LONG_TERM: 'pink',
  ADMISSION_CONFIRMED: 'green',
  ENROLLED: 'green',
  NOT_ENROLLED: 'red',
  CLOSED: 'gray',
};

/** A closed lead takes no further follow-ups. */
/** Statuses that end the follow-up cycle. A student in one of these leaves the call queue and is locked for a telecaller. */
export const FINAL_STATUSES: ReadonlySet<string> = new Set(['ADMISSION_CONFIRMED', 'ENROLLED', 'NOT_ENROLLED', 'CLOSED']);
export const CLOSED_STATUSES: ReadonlySet<string> = FINAL_STATUSES;

export const isFinalStatus = (status: string | null | undefined): boolean => FINAL_STATUSES.has(status ?? '');

export const FOLLOW_UP_LABELS: Record<string, string> = {
  AWAITING_FEEDBACK: 'Awaiting Feedback',
  BANK_LOAN: 'Bank Loan',
  FINAL_OPINION: 'Final Opinion',
  READY_TO_VISIT: 'Ready To Visit',
};

export const SOURCE_LABELS: Record<string, string> = {
  WHATSAPP: 'WhatsApp',
  WEBSITE: 'Website',
  REFERRAL: 'Referral',
  SOCIAL_MEDIA: 'Social Media',
  SHEET_DATA: 'Sheet Data',
  PREVIOUS_YEAR_LEAD: 'Previous Year Lead',
  PAPER_ADS: 'Paper Ads',
  OTHERS: 'Others',
  EMAIL: 'Email',
  DIRECT_WALKIN: 'Direct Walkin',
  DIRECT_CALL: 'Direct Call',
  CONSULTANT: 'Consultant',
  PHONE_INQUIRY: 'Phone Inquiry',
  INSTAGRAM: 'Instagram',
  FACEBOOK: 'Facebook',
  GOOGLE_ADS: 'Google Ads',
  LINKEDIN: 'LinkedIn',
  YOUTUBE: 'YouTube',
};

export const WEB_SOURCE_LABELS: Record<string, string> = {
  WEB: 'Web',
  WEBSITE: 'Website',
  LANDING_PAGE: 'Landing Page',
  GOOGLE_ADS: 'Google Ads',
};

export const INDIAN_STATES: readonly string[] = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa', 'Gujarat', 'Haryana',
  'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
  'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana',
  'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal', 'Delhi', 'Jammu and Kashmir', 'Ladakh',
  'Puducherry', 'Chandigarh',
];

// ------------------------------------------------------------------------ rows

export type StudentRow = {
  id: string;
  name?: string | null;
  phone?: { primaryPhoneNumber?: string | null; primaryPhoneCallingCode?: string | null } | null;
  email?: { primaryEmail?: string | null } | null;
  parentName?: string | null;
  alternatePhone?: string | null;
  city?: string | null;
  district?: string | null;
  state?: string | null;
  streetAddress?: string | null;
  counsellorName?: string | null;
  telecallerName?: string | null;
  nextCallAt?: string | null;
  deskStatus?: string | null;
  followUpStatus?: string | null;
  leadSource?: string | null;
  isBlocked?: boolean | null;
  isPotential?: boolean | null;
  priority?: string | null;
  preferredCountry?: string | null;
  countryInterest?: string | null;
  neetScore?: string | null;
  neetQualified?: string | null;
  budget?: string | null;
  intakeYear?: string | null;
  education?: string | null;
  passportStatus?: string | null;
  totalCalls?: number | null;
  lastCalledAt?: string | null;
  lastCallStatus?: string | null;
  lastCalledBy?: string | null;
  statusUpdatedAt?: string | null;
  statusUpdatedBy?: string | null;
  previousStatus?: string | null;
  latestRemark?: string | null;
  gender?: string | null;
  dateOfBirth?: string | null;
  courseInterest?: string | null;
  preferredUniversity?: string | null;
  parentPhone?: string | null;
  parentOccupation?: string | null;
  pincode?: string | null;
  twelfthPercentage?: string | null;
  tenthPercentage?: string | null;
  neetYear?: string | null;
  category?: string | null;
  preferredLanguage?: string | null;
  notes?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
};

export type WebLeadRow = {
  id: string;
  name?: string | null;
  phone?: string | null;
  email?: string | null;
  parentName?: string | null;
  countryInterest?: string | null;
  education?: string | null;
  groupInterest?: string | null;
  neetQualified?: string | null;
  neetScore?: string | null;
  gclid?: string | null;
  campaignId?: string | null;
  message?: string | null;
  source?: string | null;
  submittedAt?: string | null;
  status?: string | null;
  convertedLeadId?: string | null;
};

export const UNASSIGNED = 'Unassigned';

export const studentPhone = (row: StudentRow): string => (row.phone?.primaryPhoneNumber ?? '').trim();

export const initials = (name: string | null | undefined): string => {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);

  if (parts.length === 0) return '?';

  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
};

export const isClosed = (row: StudentRow): boolean => CLOSED_STATUSES.has(row.deskStatus ?? '');

// ----------------------------------------------------------------------- scope

export type DeskUser = { name: string };

/** The leads a person may see: everything for an admin, their own for the others. */
export const visibleStudents = (rows: StudentRow[], role: DeskRole, user: DeskUser): StudentRow[] => {
  if (isAdmin(role)) return rows;
  const scope = ROLE_PROFILES[role]?.scope ?? 'ALL';
  const me = user.name.trim().toLowerCase();

  if (scope === 'ALL') return rows;
  if (me === '') return [];

  const key = scope === 'OWN_COUNSELLOR' ? 'counsellorName' : 'telecallerName';

  return rows.filter((row) => (row[key] ?? '').trim().toLowerCase() === me);
};

/** Distinct people named on the leads, for the \"viewing as\" list and the filters. */
export const rosterOf = (rows: StudentRow[], key: 'counsellorName' | 'telecallerName'): string[] =>
  [...new Set(rows.map((row) => (row[key] ?? '').trim()).filter((name) => name !== ''))].sort((a, b) =>
    a.localeCompare(b),
  );

// ------------------------------------------------------------------ call queue

export type QueueTab = 'TODAY' | 'UPCOMING' | 'PENDING' | 'CLOSED';

const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
const istDay = (date: Date): string => new Date(date.getTime() + IST_OFFSET_MS).toISOString().slice(0, 10);

/** Which follow-up tab a student belongs on. Closed wins; no date means no queue. */
export const queueTab = (row: StudentRow, now: Date = new Date()): QueueTab | null => {
  if (isClosed(row)) return 'CLOSED';
  if (row.isBlocked === true) return null;
  if (!row.nextCallAt) return null;

  const due = new Date(row.nextCallAt);

  if (Number.isNaN(due.getTime())) return null;

  const dueDay = istDay(due);
  const today = istDay(now);

  if (dueDay === today) return 'TODAY';

  return dueDay > today ? 'UPCOMING' : 'PENDING';
};

export const queueCounts = (rows: StudentRow[], now: Date = new Date()): Record<QueueTab, number> => {
  const counts: Record<QueueTab, number> = { TODAY: 0, UPCOMING: 0, PENDING: 0, CLOSED: 0 };

  for (const row of rows) {
    const tab = queueTab(row, now);

    if (tab !== null) counts[tab] += 1;
  }

  return counts;
};

export const inQueue = (rows: StudentRow[], tab: QueueTab, now: Date = new Date()): StudentRow[] =>
  rows
    .filter((row) => queueTab(row, now) === tab)
    .sort((a, b) => (a.nextCallAt ?? '').localeCompare(b.nextCallAt ?? '') || (a.name ?? '').localeCompare(b.name ?? ''));

// --------------------------------------------------------------------- filters

export type SearchField =
  | ''
  | 'name'
  | 'phone'
  | 'alternatePhone'
  | 'parentName'
  | 'email'
  | 'city'
  | 'state'
  | 'district'
  | 'source'
  | 'nextCall'
  | 'notes';

export const SEARCH_FIELDS: ReadonlyArray<{ value: SearchField; label: string }> = [
  { value: '', label: 'Select Field' },
  { value: 'name', label: 'Student Name' },
  { value: 'phone', label: 'Mobile Number' },
  { value: 'alternatePhone', label: 'Alternate Phone' },
  { value: 'parentName', label: 'Parent Name' },
  { value: 'email', label: 'Email Address' },
  { value: 'city', label: 'City' },
  { value: 'state', label: 'State' },
  { value: 'district', label: 'District' },
  { value: 'source', label: 'Source' },
  { value: 'nextCall', label: 'Next Call Date' },
  { value: 'notes', label: 'Comments' },
];

export type StudentFilters = {
  counsellors: string[];
  telecallers: string[];
  statuses: string[];
  followUps: string[];
  /** 'ALL' | 'HOT' (potential) | 'STANDARD'. */
  leadType: 'ALL' | 'HOT' | 'STANDARD';
  field: SearchField;
  query: string;
};

export const NO_FILTERS: StudentFilters = {
  counsellors: [],
  telecallers: [],
  statuses: [],
  followUps: [],
  leadType: 'ALL',
  field: '',
  query: '',
};

const haystack = (row: StudentRow, field: SearchField): string => {
  switch (field) {
    case 'name': return row.name ?? '';
    case 'phone': return studentPhone(row);
    case 'alternatePhone': return row.alternatePhone ?? '';
    case 'parentName': return row.parentName ?? '';
    case 'email': return row.email?.primaryEmail ?? '';
    case 'city': return row.city ?? '';
    case 'state': return row.state ?? '';
    case 'district': return row.district ?? '';
    case 'source': return SOURCE_LABELS[row.leadSource ?? ''] ?? row.leadSource ?? '';
    case 'nextCall': return row.nextCallAt ?? '';
    case 'notes': return row.notes ?? '';
    default:
      return [row.name, studentPhone(row), row.alternatePhone, row.parentName, row.email?.primaryEmail, row.city, row.state, row.district]
        .filter(Boolean)
        .join(' ');
  }
};

const assigned = (value: string | null | undefined): string => ((value ?? '').trim() === '' ? UNASSIGNED : (value as string).trim());

export const filterStudents = (rows: StudentRow[], f: StudentFilters): StudentRow[] => {
  const q = f.query.trim().toLowerCase();

  return rows.filter((row) => {
    if (f.counsellors.length > 0 && !f.counsellors.includes(assigned(row.counsellorName))) return false;
    if (f.telecallers.length > 0 && !f.telecallers.includes(assigned(row.telecallerName))) return false;
    if (f.statuses.length > 0 && !f.statuses.includes(row.deskStatus ?? '')) return false;
    if (f.followUps.length > 0 && !f.followUps.includes(row.followUpStatus ?? '')) return false;
    if (f.leadType === 'HOT' && row.isPotential !== true) return false;
    if (f.leadType === 'STANDARD' && row.isPotential === true) return false;
    if (q !== '' && !haystack(row, f.field).toLowerCase().includes(q)) return false;

    return true;
  });
};

export const sortStudents = (rows: StudentRow[]): StudentRow[] =>
  [...rows].sort((a, b) => (a.name ?? '').toLowerCase().localeCompare((b.name ?? '').toLowerCase()));

export const paginate = <T,>(items: T[], page: number, pageSize: number): { items: T[]; pages: number; page: number } => {
  const pages = Math.max(1, Math.ceil(items.length / pageSize));
  const current = Math.min(Math.max(1, page), pages);

  return { items: items.slice((current - 1) * pageSize, current * pageSize), pages, page: current };
};

// ------------------------------------------------------------------- dashboard

export type HomeStats = {
  total: number;
  scheduledToday: number;
  potential: number;
  enrolled: number;
  pending: number;
  interested: number;
  blocked: number;
};

export const homeStats = (rows: StudentRow[], now: Date = new Date()): HomeStats => {
  const counts = queueCounts(rows, now);

  return {
    total: rows.filter((row) => row.isBlocked !== true).length,
    scheduledToday: counts.TODAY,
    potential: rows.filter((row) => row.isPotential === true || row.deskStatus === 'INTERESTED').length,
    enrolled: rows.filter((row) => row.deskStatus === 'ENROLLED' || row.deskStatus === 'ADMISSION_CONFIRMED').length,
    pending: counts.PENDING,
    interested: rows.filter((row) => row.deskStatus === 'INTERESTED').length,
    blocked: rows.filter((row) => row.isBlocked === true).length,
  };
};

export type FunnelRow = { label: string; count: number; share: number };

/** The four-step funnel on the counsellor home. */
export const leadFunnel = (rows: StudentRow[], now: Date = new Date()): FunnelRow[] => {
  const total = Math.max(rows.length, 1);
  const steps: Array<[string, number]> = [
    ['New Inquiry', rows.filter((r) => r.deskStatus === 'NEW' || r.deskStatus === 'NEW_LEAD').length],
    ['Interested', rows.filter((r) => r.deskStatus === 'INTERESTED').length],
    ['Follow-ups Needed', rows.filter((r) => r.deskStatus === 'FOLLOW_UP_REQUIRED' || queueTab(r, now) === 'PENDING').length],
    ['Enrolled', rows.filter((r) => r.deskStatus === 'ENROLLED' || r.deskStatus === 'ADMISSION_CONFIRMED').length],
  ];

  return steps.map(([label, count]) => ({ label, count, share: Math.round((count / total) * 100) }));
};

export const sourceBreakdown = (rows: StudentRow[]): Array<{ key: string; label: string; count: number; share: number }> => {
  const total = Math.max(rows.length, 1);
  const counts = new Map<string, number>();

  for (const row of rows) {
    const key = row.leadSource ?? 'OTHERS';

    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  return [...counts.entries()]
    .map(([key, count]) => ({ key, label: SOURCE_LABELS[key] ?? key, count, share: Math.round((count / total) * 100) }))
    .sort((a, b) => b.count - a.count);
};

// --------------------------------------------------------------------- reports

export type DateRange = 'ALL' | 'TODAY' | 'WEEK' | 'MONTH' | 'QUARTER' | 'CUSTOM';

export const DATE_RANGES: ReadonlyArray<{ value: DateRange; label: string }> = [
  { value: 'ALL', label: 'All Time' },
  { value: 'TODAY', label: 'Today' },
  { value: 'WEEK', label: 'This Week' },
  { value: 'MONTH', label: 'This Month' },
  { value: 'QUARTER', label: 'This Quarter' },
  { value: 'CUSTOM', label: 'Custom Range' },
];

const startOfRange = (range: DateRange, now: Date): Date | null => {
  const ist = new Date(now.getTime() + IST_OFFSET_MS);
  const day = new Date(Date.UTC(ist.getUTCFullYear(), ist.getUTCMonth(), ist.getUTCDate()));
  let start: Date;

  switch (range) {
    case 'ALL': return null;
    case 'TODAY': start = day; break;
    case 'WEEK': start = new Date(day.getTime() - day.getUTCDay() * 86_400_000); break;
    case 'MONTH': start = new Date(Date.UTC(ist.getUTCFullYear(), ist.getUTCMonth(), 1)); break;
    case 'CUSTOM': return null;
    default: start = new Date(Date.UTC(ist.getUTCFullYear(), Math.floor(ist.getUTCMonth() / 3) * 3, 1));
  }

  return new Date(start.getTime() - IST_OFFSET_MS);
};

export type CustomRange = { from: string; to: string };

/** A custom range is two IST calendar days (YYYY-MM-DD), both inclusive. Either end may be left empty. */
const customBounds = (custom: CustomRange | undefined): { start: Date | null; end: Date | null } => {
  const day = (value: string | undefined, endOfDay: boolean): Date | null => {
    if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;

    const [y, m, d] = value.split('-').map(Number);

    return new Date(Date.UTC(y, m - 1, d, endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0, endOfDay ? 999 : 0) - IST_OFFSET_MS);
  };

  return { start: day(custom?.from, false), end: day(custom?.to, true) };
};

export const customRangeError = (custom: CustomRange): string | null => {
  const { start, end } = customBounds(custom);

  if (start !== null && end !== null && start > end) return 'The start date is after the end date.';

  return null;
};

export const inDateRange = (rows: StudentRow[], range: DateRange, now: Date = new Date(), custom?: CustomRange): StudentRow[] => {
  if (range === 'CUSTOM') {
    const { start, end } = customBounds(custom);

    return rows.filter((row) => {
      if (!row.createdAt) return false;

      const created = new Date(row.createdAt);

      return (start === null || created >= start) && (end === null || created <= end);
    });
  }

  const start = startOfRange(range, now);

  if (start === null) return rows;

  return rows.filter((row) => row.createdAt !== null && row.createdAt !== undefined && new Date(row.createdAt) >= start);
};

export const pipelineCounts = (rows: StudentRow[]): Array<{ key: string; label: string; count: number }> =>
  DESK_STATUS_ORDER.map((key) => ({
    key,
    label: DESK_STATUS_LABELS[key],
    count: rows.filter((row) => row.deskStatus === key).length,
  }));

export type CounsellorLoad = { name: string; assigned: number; enrolled: number };

export const counsellorLoad = (rows: StudentRow[]): CounsellorLoad[] => {
  const byName = new Map<string, CounsellorLoad>();

  for (const row of rows) {
    const name = assigned(row.counsellorName);
    const entry = byName.get(name) ?? { name, assigned: 0, enrolled: 0 };

    entry.assigned += 1;
    if (row.deskStatus === 'ENROLLED' || row.deskStatus === 'ADMISSION_CONFIRMED') entry.enrolled += 1;
    byName.set(name, entry);
  }

  return [...byName.values()].sort((a, b) => b.assigned - a.assigned);
};

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

export const weeklyInflow = (rows: StudentRow[]): Array<{ day: string; count: number }> => {
  const counts = [0, 0, 0, 0, 0, 0, 0];

  for (const row of rows) {
    if (!row.createdAt) continue;

    const created = new Date(row.createdAt);

    if (Number.isNaN(created.getTime())) continue;

    counts[new Date(created.getTime() + IST_OFFSET_MS).getUTCDay()] += 1;
  }

  return WEEKDAYS.map((day, index) => ({ day, count: counts[index] }));
};

// ------------------------------------------------------------------- web leads

export type WebLeadFilters = { query: string; status: string; source: string };

export const filterWebLeads = (rows: WebLeadRow[], f: WebLeadFilters): WebLeadRow[] => {
  const q = f.query.trim().toLowerCase();

  return rows
    .filter((row) => {
      if (f.status !== '' && (row.status ?? 'NEW') !== f.status) return false;
      if (f.source !== '' && (row.source ?? '') !== f.source) return false;
      if (q === '') return true;

      return [row.name, row.phone, row.email, row.message, row.countryInterest].some((v) => (v ?? '').toLowerCase().includes(q));
    })
    .sort((a, b) => (b.submittedAt ?? '').localeCompare(a.submittedAt ?? ''));
};

/** Map a Web Lead's free-text source onto the Student Lead's lead-source options. */
export const studentSourceForWebLead = (source: string | null | undefined): string => {
  switch (source) {
    case 'GOOGLE_ADS': return 'GOOGLE_ADS';
    case 'LANDING_PAGE':
    case 'WEB':
    case 'WEBSITE':
    default: return 'WEBSITE';
  }
};

const digitsOnly = (value: string | null | undefined): string => (value ?? '').replace(/\D/g, '');

/** The last ten digits, which is how an Indian mobile number is compared. */
export const normalisePhone = (value: string | null | undefined): string => digitsOnly(value).slice(-10);

/** What a converted Web Lead becomes. Fields the form never had stay empty. */
export const studentFromWebLead = (lead: WebLeadRow): Record<string, unknown> => {
  const phone = normalisePhone(lead.phone);
  const notes = [
    lead.message ? `Form message: ${lead.message}` : '',
    lead.education ? `Education: ${lead.education}` : '',
    lead.groupInterest ? `Group interest: ${lead.groupInterest}` : '',
    lead.campaignId ? `Campaign: ${lead.campaignId}` : '',
  ]
    .filter(Boolean)
    .join('\n');

  return {
    name: (lead.name ?? '').trim(),
    phone: { primaryPhoneNumber: phone, primaryPhoneCallingCode: '+91', primaryPhoneCountryCode: 'IN' },
    ...(lead.email ? { email: { primaryEmail: lead.email.trim() } } : {}),
    ...(lead.parentName ? { parentName: lead.parentName.trim() } : {}),
    preferredCountry: (lead.countryInterest ?? '').trim() || null,
    leadSource: studentSourceForWebLead(lead.source),
    deskStatus: 'NEW_LEAD',
    notes,
  };
};

/** Is there already a student with this number? A form should not create a second one. */
export const findDuplicate = (rows: StudentRow[], phone: string | null | undefined): StudentRow | null => {
  const wanted = normalisePhone(phone);

  if (wanted.length < 10) return null;

  return rows.find((row) => normalisePhone(studentPhone(row)) === wanted) ?? null;
};

// ------------------------------------------------------------------ add / edit

export type StudentForm = {
  phone: string;
  name: string;
  parentName: string;
  alternatePhone: string;
  email: string;
  city: string;
  state: string;
  district: string;
  address: string;
  deskStatus: string;
  leadSource: string;
  isPotential: boolean;
  priority: string;
  preferredCountry: string;
  neetScore: string;
  neetQualified: string;
  budget: string;
  intakeYear: string;
  education: string;
  passportStatus: string;
  gender: string;
  dateOfBirth: string;
  courseInterest: string;
  preferredUniversity: string;
  parentPhone: string;
  parentOccupation: string;
  pincode: string;
  twelfthPercentage: string;
  tenthPercentage: string;
  neetYear: string;
  category: string;
  preferredLanguage: string;
  counsellorName: string;
  telecallerName: string;
  nextCallAt: string;
  notes: string;
  followUpStatus: string;
};

export const EMPTY_FORM: StudentForm = {
  phone: '',
  name: '',
  parentName: '',
  alternatePhone: '',
  email: '',
  city: '',
  state: '',
  district: '',
  address: '',
  deskStatus: 'NEW',
  leadSource: 'SHEET_DATA',
  isPotential: false,
  priority: 'MEDIUM',
  preferredCountry: '',
  neetScore: '',
  neetQualified: '',
  budget: '',
  intakeYear: '',
  education: '',
  passportStatus: '',
  gender: '',
  dateOfBirth: '',
  courseInterest: '',
  preferredUniversity: '',
  parentPhone: '',
  parentOccupation: '',
  pincode: '',
  twelfthPercentage: '',
  tenthPercentage: '',
  neetYear: '',
  category: '',
  preferredLanguage: '',
  counsellorName: '',
  telecallerName: '',
  nextCallAt: '',
  notes: '',
  followUpStatus: '',
};

export type FormErrors = Partial<Record<keyof StudentForm, string>>;

export const validateStudentForm = (form: StudentForm, options: { requireCounsellor?: boolean } = {}): FormErrors => {
  const errors: FormErrors = {};

  if (normalisePhone(form.phone).length !== 10) errors.phone = 'Enter the 10-digit mobile number.';
  if (form.name.trim() === '') errors.name = 'Enter the student name.';
  if (form.alternatePhone.trim() !== '' && normalisePhone(form.alternatePhone).length !== 10) {
    errors.alternatePhone = 'Enter a 10-digit number, or leave this empty.';
  }
  if (form.email.trim() !== '' && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) {
    errors.email = 'That email address does not look right.';
  }
  if (options.requireCounsellor === true && form.counsellorName.trim() === '') errors.counsellorName = 'A student needs a counsellor.';

  return errors;
};

/** The record fields for a form. Empty text is left out so it never overwrites a value. */
export const studentInputFromForm = (form: StudentForm): Record<string, unknown> => {
  const text = (value: string): string | undefined => (value.trim() === '' ? undefined : value.trim());
  const data: Record<string, unknown> = {
    name: form.name.trim(),
    phone: { primaryPhoneNumber: normalisePhone(form.phone), primaryPhoneCallingCode: '+91', primaryPhoneCountryCode: 'IN' },
    deskStatus: form.deskStatus || 'NEW',
    leadSource: form.leadSource || 'SHEET_DATA',
    isPotential: form.isPotential,
    counsellorName: form.counsellorName.trim(),
    stage: 'NEW_LEAD',
  };
  const optional: Record<string, string | undefined> = {
    parentName: text(form.parentName),
    alternatePhone: text(form.alternatePhone) === undefined ? undefined : normalisePhone(form.alternatePhone),
    city: text(form.city),
    state: text(form.state),
    district: text(form.district),
    streetAddress: text(form.address),
    telecallerName: text(form.telecallerName),
    notes: text(form.notes),
    followUpStatus: text(form.followUpStatus),
    priority: text(form.priority) ?? 'MEDIUM',
    preferredCountry: text(form.preferredCountry),
    countryInterest: text(form.preferredCountry),
    neetScore: text(form.neetScore),
    neetQualified: text(form.neetQualified),
    budget: text(form.budget),
    intakeYear: text(form.intakeYear),
    education: text(form.education),
    passportStatus: text(form.passportStatus),
    gender: text(form.gender),
    dateOfBirth: text(form.dateOfBirth),
    courseInterest: text(form.courseInterest),
    preferredUniversity: text(form.preferredUniversity),
    parentPhone: text(form.parentPhone) === undefined ? undefined : normalisePhone(form.parentPhone),
    parentOccupation: text(form.parentOccupation),
    pincode: text(form.pincode),
    twelfthPercentage: text(form.twelfthPercentage),
    tenthPercentage: text(form.tenthPercentage),
    neetYear: text(form.neetYear),
    category: text(form.category),
    preferredLanguage: text(form.preferredLanguage),
    nextCallAt: form.nextCallAt.trim() === '' ? undefined : new Date(form.nextCallAt).toISOString(),
  };

  for (const [key, value] of Object.entries(optional)) if (value !== undefined) data[key] = value;

  if (form.email.trim() !== '') data.email = { primaryEmail: form.email.trim() };

  return data;
};

// ---------------------------------------------------------------------- export

const csvCell = (value: unknown): string => {
  const text = value === null || value === undefined ? '' : String(value);

  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const STUDENT_CSV_HEADERS = [
  'Name', 'Mobile', 'Parent', 'Email', 'City', 'District', 'State', 'Status', 'Source', 'Counsellor', 'Telecaller', 'Next call',
] as const;

export const studentsToCsv = (rows: StudentRow[]): string =>
  [
    STUDENT_CSV_HEADERS.join(','),
    ...rows.map((row) =>
      [
        row.name, studentPhone(row), row.parentName, row.email?.primaryEmail, row.city, row.district, row.state,
        DESK_STATUS_LABELS[row.deskStatus ?? ''] ?? row.deskStatus, SOURCE_LABELS[row.leadSource ?? ''] ?? row.leadSource,
        row.counsellorName, row.telecallerName, row.nextCallAt,
      ]
        .map(csvCell)
        .join(','),
    ),
  ].join('\n');

export const webLeadsToCsv = (rows: WebLeadRow[]): string =>
  [
    ['Name', 'Mobile', 'Email', 'Countries', 'Education', 'Source', 'Submitted', 'Status'].join(','),
    ...rows.map((row) =>
      [row.name, row.phone, row.email, row.countryInterest, row.education, WEB_SOURCE_LABELS[row.source ?? ''] ?? row.source, row.submittedAt, row.status ?? 'NEW']
        .map(csvCell)
        .join(','),
    ),
  ].join('\n');

/** Format a due date the way the desk shows it: 07-Jan-2027 11:33 PM (IST). */
export const formatCallDate = (value: string | null | undefined): string => {
  if (!value) return 'No call scheduled';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return 'No call scheduled';

  const shifted = new Date(date.getTime() + IST_OFFSET_MS);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const hour24 = shifted.getUTCHours();
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;

  return `${String(shifted.getUTCDate()).padStart(2, '0')}-${months[shifted.getUTCMonth()]}-${shifted.getUTCFullYear()} ${String(hour12).padStart(2, '0')}:${String(shifted.getUTCMinutes()).padStart(2, '0')} ${hour24 < 12 ? 'AM' : 'PM'}`;
};

// ------------------------------------------------------------------- edit form

const pad2 = (n: number): string => String(n).padStart(2, '0');

/** An ISO instant as the value a datetime-local input expects, in the browser's own zone. */
export const toLocalInput = (iso: string | null | undefined): string => {
  if (!iso) return '';

  const d = new Date(iso);

  if (Number.isNaN(d.getTime())) return '';

  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

export const formFromStudent = (row: StudentRow): StudentForm => ({
  phone: studentPhone(row),
  name: row.name ?? '',
  parentName: row.parentName ?? '',
  alternatePhone: row.alternatePhone ?? '',
  email: row.email?.primaryEmail ?? '',
  city: row.city ?? '',
  state: row.state ?? '',
  district: row.district ?? '',
  address: row.streetAddress ?? '',
  deskStatus: row.deskStatus ?? 'NEW',
  leadSource: row.leadSource ?? 'SHEET_DATA',
  isPotential: row.isPotential === true,
  priority: row.priority ?? 'MEDIUM',
  preferredCountry: row.preferredCountry ?? row.countryInterest ?? '',
  neetScore: row.neetScore ?? '',
  neetQualified: row.neetQualified ?? '',
  budget: row.budget ?? '',
  intakeYear: row.intakeYear ?? '',
  education: row.education ?? '',
  passportStatus: row.passportStatus ?? '',
  gender: row.gender ?? '',
  dateOfBirth: row.dateOfBirth ?? '',
  courseInterest: row.courseInterest ?? '',
  preferredUniversity: row.preferredUniversity ?? '',
  parentPhone: row.parentPhone ?? '',
  parentOccupation: row.parentOccupation ?? '',
  pincode: row.pincode ?? '',
  twelfthPercentage: row.twelfthPercentage ?? '',
  tenthPercentage: row.tenthPercentage ?? '',
  neetYear: row.neetYear ?? '',
  category: row.category ?? '',
  preferredLanguage: row.preferredLanguage ?? '',
  counsellorName: row.counsellorName ?? '',
  telecallerName: row.telecallerName ?? '',
  nextCallAt: toLocalInput(row.nextCallAt),
  notes: row.notes ?? '',
  followUpStatus: row.followUpStatus ?? '',
});

/**
 * An edit writes every field, so a cleared box really clears the value (the add form
 * leaves empty fields out instead). The stage is not touched: that belongs to the
 * counselling pipeline, not the desk form.
 */
export const studentUpdateFromForm = (form: StudentForm): Record<string, unknown> => {
  const base = studentInputFromForm(form);

  delete base.stage;

  return {
    ...base,
    parentName: form.parentName.trim() === '' ? null : form.parentName.trim(),
    alternatePhone: form.alternatePhone.trim() === '' ? null : normalisePhone(form.alternatePhone),
    city: form.city.trim() === '' ? null : form.city.trim(),
    state: form.state.trim() === '' ? null : form.state.trim(),
    district: form.district.trim() === '' ? null : form.district.trim(),
    streetAddress: form.address.trim() === '' ? null : form.address.trim(),
    telecallerName: form.telecallerName.trim() === '' ? null : form.telecallerName.trim(),
    notes: form.notes.trim() === '' ? null : form.notes.trim(),
    followUpStatus: form.followUpStatus === '' ? null : form.followUpStatus,
    priority: form.priority.trim() === '' ? 'MEDIUM' : form.priority.trim(),
    preferredCountry: form.preferredCountry.trim() === '' ? null : form.preferredCountry.trim(),
    countryInterest: form.preferredCountry.trim() === '' ? null : form.preferredCountry.trim(),
    neetScore: form.neetScore.trim() === '' ? null : form.neetScore.trim(),
    neetQualified: form.neetQualified.trim() === '' ? null : form.neetQualified.trim(),
    budget: form.budget.trim() === '' ? null : form.budget.trim(),
    intakeYear: form.intakeYear.trim() === '' ? null : form.intakeYear.trim(),
    education: form.education.trim() === '' ? null : form.education.trim(),
    passportStatus: form.passportStatus.trim() === '' ? null : form.passportStatus.trim(),
    gender: form.gender.trim() === '' ? null : form.gender.trim(),
    dateOfBirth: form.dateOfBirth.trim() === '' ? null : form.dateOfBirth.trim(),
    courseInterest: form.courseInterest.trim() === '' ? null : form.courseInterest.trim(),
    preferredUniversity: form.preferredUniversity.trim() === '' ? null : form.preferredUniversity.trim(),
    parentPhone: form.parentPhone.trim() === '' ? null : normalisePhone(form.parentPhone),
    parentOccupation: form.parentOccupation.trim() === '' ? null : form.parentOccupation.trim(),
    pincode: form.pincode.trim() === '' ? null : form.pincode.trim(),
    twelfthPercentage: form.twelfthPercentage.trim() === '' ? null : form.twelfthPercentage.trim(),
    tenthPercentage: form.tenthPercentage.trim() === '' ? null : form.tenthPercentage.trim(),
    neetYear: form.neetYear.trim() === '' ? null : form.neetYear.trim(),
    category: form.category.trim() === '' ? null : form.category.trim(),
    preferredLanguage: form.preferredLanguage.trim() === '' ? null : form.preferredLanguage.trim(),
    nextCallAt: form.nextCallAt.trim() === '' ? null : new Date(form.nextCallAt).toISOString(),
    email: form.email.trim() === '' ? { primaryEmail: '' } : { primaryEmail: form.email.trim() },
  };
};


// ------------------------------------------------------------------ csv import

/** The header row a student import file must start with, in this order. */
export const IMPORT_HEADERS = [
  'Mobile',
  'Name',
  'Parent',
  'Parent Phone',
  'Parent Occupation',
  'Alternate Phone',
  'Email',
  'Gender',
  'Date of Birth',
  'City',
  'District',
  'State',
  'Pincode',
  'Address',
  'Course Interest',
  'Preferred Country',
  'Preferred University',
  'NEET Score',
  'NEET Qualified',
  'NEET Year',
  '12th Percentage',
  '10th Percentage',
  'Category',
  'Preferred Language',
  'Budget',
  'Intake Year',
  'Education',
  'Passport Status',
  'Priority',
  'Status',
  'Source',
  'Counsellor',
  'Telecaller',
  'Comments',
] as const;

export const importTemplateCsv = (): string =>
  `${IMPORT_HEADERS.join(',')}\n9876543210,Aarav Sharma,Sunil Sharma,9876543212,Business,9876543211,aarav@example.com,Male,2007-05-14,Hyderabad,Hyderabad,Telangana,500034,Banjara Hills,MBBS,Russia,Osh State University,480,Yes,2026,82%,88%,General,Telugu,20-25 Lakhs,2026,12th PCB 82%,Available,High,New,Sheet Data,,,Interested in MBBS in Russia`;

/** RFC-4180 style: quoted cells may hold commas, doubled quotes and line breaks. */
export const parseCsv = (text: string): string[][] => {
  const rows: string[][] = [];
  let cell = '';
  let row: string[] = [];
  let quoted = false;

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];

    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') { cell += '"'; i += 1; }
      else if (ch === '"') quoted = false;
      else cell += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ',') { row.push(cell); cell = ''; }
    else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i += 1;
      row.push(cell); cell = '';
      if (row.some((c) => c.trim() !== '')) rows.push(row);
      row = [];
    } else cell += ch;
  }

  row.push(cell);
  if (row.some((c) => c.trim() !== '')) rows.push(row);

  return rows;
};

const reverseLabel = (labels: Record<string, string>, text: string, fallback: string): string => {
  const wanted = text.trim().toLowerCase();

  if (wanted === '') return fallback;

  const hit = Object.entries(labels).find(([key, label]) => label.toLowerCase() === wanted || key.toLowerCase() === wanted.replace(/[\s-]+/g, '_'));

  return hit === undefined ? fallback : hit[0];
};

export type ImportResult = { forms: StudentForm[]; rejected: Array<{ line: number; reason: string }> };

/**
 * Read an import file into forms. Every row goes through the same validation as the Add
 * Student screen, a row that fails is reported with its line number, and a number that
 * appears twice in the file is kept once.
 */
export const parseStudentImport = (text: string, defaultCounsellor: string): ImportResult => {
  const table = parseCsv(text.replace(/^\uFEFF/, ''));
  const forms: StudentForm[] = [];
  const rejected: Array<{ line: number; reason: string }> = [];

  if (table.length === 0) return { forms, rejected: [{ line: 1, reason: 'The file is empty.' }] };

  const header = table[0].map((h) => h.trim().toLowerCase());
  const col = (...aliases: string[]): number => {
    for (const a of aliases) {
      const idx = header.indexOf(a.toLowerCase());
      if (idx !== -1) return idx;
    }
    return -1;
  };

  const missing = ['Mobile', 'Name'].filter((name) => {
    if (name === 'Mobile') return col('mobile', 'phone', 'contact', 'phone number', 'mobile number') === -1;
    return col('name', 'student name', 'full name', 'candidate name') === -1;
  });

  if (missing.length > 0) return { forms, rejected: [{ line: 1, reason: `Missing required column: ${missing.join(', ')}. Use the template.` }] };

  const seen = new Set<string>();

  table.slice(1).forEach((cells, index) => {
    const line = index + 2;
    const get = (...aliases: string[]): string => {
      const idx = col(...aliases);
      return idx === -1 ? '' : (cells[idx] ?? '').trim();
    };

    const form: StudentForm = {
      ...EMPTY_FORM,
      phone: get('mobile', 'phone', 'contact', 'phone number', 'mobile number'),
      name: get('name', 'student name', 'full name', 'candidate name'),
      parentName: get('parent', 'parent name', 'father name', 'father', 'mother name', 'guardian'),
      alternatePhone: get('alternate phone', 'alternate mobile', 'alternate number', 'whatsapp', 'whatsapp number'),
      email: get('email', 'email id', 'email address'),
      city: get('city', 'town'),
      state: get('state', 'province'),
      district: get('district'),
      address: get('address', 'street address', 'location'),
      preferredCountry: get('preferred country', 'country', 'target country', 'country interest'),
      neetScore: get('neet score', 'neet marks', 'neet'),
      neetQualified: get('neet qualified', 'qualified'),
      budget: get('budget', 'budget range', 'expected budget'),
      intakeYear: get('intake year', 'intake', 'academic year', 'batch', 'year'),
      education: get('education', 'qualification', '12th marks', '12th %', 'pcb %'),
      passportStatus: get('passport status', 'passport'),
      gender: get('gender', 'sex'),
      dateOfBirth: get('date of birth', 'dob', 'birth date'),
      courseInterest: get('course', 'course interest', 'program', 'degree'),
      preferredUniversity: get('university', 'preferred university', 'target university', 'college'),
      parentPhone: get('parent phone', 'parent mobile', 'father phone', 'father mobile'),
      parentOccupation: get('parent occupation', 'father occupation', 'occupation'),
      pincode: get('pincode', 'pin code', 'postal code', 'zip'),
      twelfthPercentage: get('12th percentage', '12th %', '12th marks', 'pcb %', 'intermediate %'),
      tenthPercentage: get('10th percentage', '10th %', '10th marks', 'ssc %'),
      neetYear: get('neet year', 'exam year'),
      category: get('category', 'caste category', 'quota'),
      preferredLanguage: get('language', 'preferred language', 'mother tongue'),
      priority: get('priority', 'lead priority') || 'MEDIUM',
      deskStatus: reverseLabel(DESK_STATUS_LABELS, get('status', 'desk status', 'lead status'), 'NEW'),
      followUpStatus: reverseLabel(FOLLOW_UP_LABELS, get('follow-up status', 'followup status', 'sub status'), ''),
      leadSource: reverseLabel(SOURCE_LABELS, get('source', 'lead source', 'channel'), 'SHEET_DATA'),
      counsellorName: get('counsellor', 'counselor', 'assigned counsellor', 'assigned counselor') || defaultCounsellor,
      telecallerName: get('telecaller', 'caller', 'assigned telecaller'),
      notes: get('comments', 'notes', 'remarks', 'remark'),
    };
    const errors = validateStudentForm(form);
    const first = Object.values(errors)[0];

    if (first !== undefined) return void rejected.push({ line, reason: first });

    const key = normalisePhone(form.phone);

    if (seen.has(key)) return void rejected.push({ line, reason: 'This number is already earlier in the file.' });

    seen.add(key);
    forms.push(form);
  });

  return { forms, rejected };
};


// --------------------------------------------------------------- inline changes

/** An inline dropdown's choice as the value to store: "Unassigned" clears the field. */
export const assignmentValue = (choice: string): string | null => (choice === UNASSIGNED || choice.trim() === '' ? null : choice.trim());

/** A record the desk touched after it was created, within the last week: drawn with an "Updated" badge. */
export const isRecentlyUpdated = (row: StudentRow, now: Date = new Date()): boolean => {
  if (!row.updatedAt || !row.createdAt) return false;

  const updated = new Date(row.updatedAt).getTime();
  const created = new Date(row.createdAt).getTime();

  return !Number.isNaN(updated) && !Number.isNaN(created) && updated - created > 60_000 && now.getTime() - updated < 7 * 86_400_000;
};

/** The students a chart bar stands for, so clicking the bar and reading the table agree. */
export const studentsWithStatus = (rows: StudentRow[], status: string): StudentRow[] => rows.filter((row) => row.deskStatus === status);

/** What an export menu entry covers. */
export type ExportScope = 'FILTERED' | 'SELECTED' | 'ALL';

export const exportRows = (scope: ExportScope, filtered: StudentRow[], selectedIds: string[], all: StudentRow[]): StudentRow[] => {
  if (scope === 'ALL') return all;
  if (scope === 'SELECTED') return all.filter((row) => selectedIds.includes(row.id));

  return filtered;
};

// ------------------------------------------------------------------ desk users

export type UserRow = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: DeskRole | null;
  isActive: boolean;
  /** For a telecaller: the id of the counsellor they work under. */
  reportsTo?: string | null;
  createdAt?: string | null;
};

export const userFullName = (user: Pick<UserRow, 'firstName' | 'lastName'>): string => `${user.firstName} ${user.lastName}`.trim();

export const USER_ROLE_LABELS: Record<DeskRole, string> = { SUPER_ADMIN: 'Super Admin', ADMIN: 'Tenant Admin', TENANT_ADMIN: 'Tenant Admin', COUNSELLOR: 'Counsellor', TELECALLER: 'Telecaller' };

export type UserForm = { firstName: string; lastName: string; email: string; role: DeskRole | ''; password: string; confirmPassword: string; isActive: boolean; reportsTo: string };

export const EMPTY_USER_FORM: UserForm = { firstName: '', lastName: '', email: '', role: '', password: '', confirmPassword: '', isActive: true, reportsTo: '' };

export type UserFormErrors = Partial<Record<keyof UserForm, string>>;

export const MIN_PASSWORD = 8;

/** `creating` demands a password; an edit may leave it blank to keep the current one. */
export const validateUserForm = (form: UserForm, creating: boolean): UserFormErrors => {
  const errors: UserFormErrors = {};

  if (form.firstName.trim() === '') errors.firstName = 'Enter the first name.';
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) errors.email = 'Enter a valid email address.';
  if (form.role === '') errors.role = 'Choose a role.';
  if (creating && form.password === '') errors.password = 'Set a password.';
  if (form.password !== '' && form.password.length < MIN_PASSWORD) errors.password = `Use at least ${MIN_PASSWORD} characters.`;
  if (form.password !== form.confirmPassword) errors.confirmPassword = 'The two passwords do not match.';
  if (form.role === 'TELECALLER' && form.reportsTo === '') errors.reportsTo = 'Choose the counsellor this telecaller works under.';

  return errors;
};

/** Who may be removed: never the last active admin, and never someone students still depend on. */
export const userRemovalBlock = (user: UserRow, all: UserRow[], students: StudentRow[]): string | null => {
  const name = userFullName(user).toLowerCase();
  const held = students.filter((s) => [s.counsellorName, s.telecallerName].some((n) => (n ?? '').trim().toLowerCase() === name)).length;

  if (user.role === 'ADMIN' && user.isActive && all.filter((u) => u.role === 'ADMIN' && u.isActive && u.id !== user.id).length === 0) {
    return 'This is the last active admin. Make someone else an admin first.';
  }
  const team = all.filter((u) => u.role === 'TELECALLER' && u.reportsTo === user.id).length;

  if (team > 0) return `${team} telecaller${team === 1 ? ' works' : 's work'} under ${userFullName(user)}. Move ${team === 1 ? 'them' : 'them'} to another counsellor first.`;
  if (held > 0) return `${held} student${held === 1 ? ' is' : 's are'} assigned to ${userFullName(user)}. Reassign them first, or deactivate this user instead.`;

  return null;
};

/** The names a role's "Signed in as" list and the filters offer: active desk users first, then anyone only named on students. */
export const rosterFromUsers = (users: UserRow[], role: 'COUNSELLOR' | 'TELECALLER', named: string[]): string[] => {
  const fromUsers = users.filter((u) => u.role === role && u.isActive).map(userFullName);
  const seen = new Set(fromUsers.map((n) => n.toLowerCase()));

  return [...fromUsers, ...named.filter((n) => !seen.has(n.toLowerCase()))].sort((a, b) => a.localeCompare(b));
};

// ------------------------------------------------------------------ my account

export type ProfileForm = { firstName: string; lastName: string; password: string };

export const validateProfile = (form: ProfileForm): Partial<Record<keyof ProfileForm, string>> => {
  const errors: Partial<Record<keyof ProfileForm, string>> = {};

  if (form.firstName.trim() === '') errors.firstName = 'Enter your first name.';
  if (form.lastName.trim() === '') errors.lastName = 'Enter your last name.';
  if (form.password !== '' && form.password.length < MIN_PASSWORD) errors.password = `Use at least ${MIN_PASSWORD} characters, or leave this empty to keep your password.`;

  return errors;
};


/** How many students a person holds, as counsellor or telecaller (the Assign to Leads column). Matched by name, ignoring case. */
export const assignedCount = (user: Pick<UserRow, 'firstName' | 'lastName' | 'role'>, students: StudentRow[]): number => {
  const name = userFullName(user).toLowerCase();

  if (name === '' || user.role === 'ADMIN') return 0;

  const key = user.role === 'TELECALLER' ? 'telecallerName' : 'counsellorName';

  return students.filter((s) => (s[key] ?? '').trim().toLowerCase() === name).length;
};

/** The desk account a role acts as: the first active admin for the admin role, otherwise the user matching the chosen name. */
export const accountFor = (users: UserRow[], role: DeskRole, actingAs: string): UserRow | null => {
  if (isAdmin(role)) return users.find((u) => isAdmin(u.role) && u.isActive) ?? null;

  const wanted = actingAs.trim().toLowerCase();

  return wanted === '' ? null : (users.find((u) => u.role === role && userFullName(u).toLowerCase() === wanted) ?? null);
};

// ----------------------------------------------------------------------- folders

export type FolderRow = { id: string; name: string; createdAt?: string | null; fileCount?: number };

export type FileRow = { id: string; folderId: string; name: string; size: number; type?: string | null; uploadedAt: string };

export const MAX_FILE_BYTES = 5 * 1024 * 1024;
export const ALLOWED_FILE_EXTENSIONS: readonly string[] = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'csv', 'png', 'jpg', 'jpeg'];
export const MAX_FOLDER_NAME = 60;

export const fileExtension = (name: string): string => {
  const dot = name.lastIndexOf('.');

  return dot < 0 ? '' : name.slice(dot + 1).toLowerCase();
};

export const formatBytes = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(bytes < 10 * 1024 ? 1 : 0)} KB`;

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const validateFolderName = (name: string, existing: FolderRow[]): string | null => {
  const clean = name.trim();

  if (clean === '') return 'Enter a folder name.';
  if (clean.length > MAX_FOLDER_NAME) return `Use ${MAX_FOLDER_NAME} characters or fewer.`;
  if (/[\\/:*?"<>|]/.test(clean)) return 'A folder name cannot contain \\ / : * ? " < > |';
  if (existing.some((f) => f.name.trim().toLowerCase() === clean.toLowerCase())) return 'A folder with this name already exists.';

  return null;
};

export const validateUpload = (name: string, size: number): string | null => {
  const ext = fileExtension(name);

  if (!ALLOWED_FILE_EXTENSIONS.includes(ext)) return `${name}: this file type is not allowed. Use PDF, Word, Excel, CSV or an image.`;
  if (size <= 0) return `${name}: the file is empty.`;
  if (size > MAX_FILE_BYTES) return `${name}: ${formatBytes(size)} is over the ${formatBytes(MAX_FILE_BYTES)} limit.`;

  return null;
};

/** A folder that still holds files is not deleted, so an upload is never lost by accident. */
export const folderDeleteBlock = (folder: FolderRow, files: FileRow[]): string | null => {
  const held = files.filter((f) => f.folderId === folder.id).length;

  return held > 0 ? `${folder.name} still holds ${held} file${held === 1 ? '' : 's'}. Delete ${held === 1 ? 'it' : 'them'} first.` : null;
};

export type FileSort = 'DATE' | 'NAME' | 'SIZE';

export const FILE_SORTS: ReadonlyArray<{ value: FileSort; label: string }> = [
  { value: 'DATE', label: 'Upload Date' },
  { value: 'NAME', label: 'File Name' },
  { value: 'SIZE', label: 'File Size' },
];

export const visibleFiles = (files: FileRow[], folderId: string, query: string, sort: FileSort): FileRow[] => {
  const q = query.trim().toLowerCase();

  return files
    .filter((f) => f.folderId === folderId && (q === '' || f.name.toLowerCase().includes(q)))
    .sort((a, b) => {
      if (sort === 'NAME') return a.name.localeCompare(b.name);
      if (sort === 'SIZE') return b.size - a.size || a.name.localeCompare(b.name);

      return b.uploadedAt.localeCompare(a.uploadedAt) || a.name.localeCompare(b.name);
    });
};

/** Keys the bar chart reports back when a bar is clicked: the status, so the table below can filter to it. */
export const toggleStatusFilter = (current: string, clicked: string): string => (current === clicked ? '' : clicked);

// ------------------------------------------------------------- status and export

/** Moving a student to a closed status also drops the next call: a closed lead takes no further follow-ups. */
export const statusChangePatch = (status: string): Record<string, unknown> =>
  CLOSED_STATUSES.has(status) ? { deskStatus: status, nextCallAt: null } : { deskStatus: status };

export const EXPORT_SCOPES: ReadonlyArray<{ value: ExportScope; label: string; file: string }> = [
  { value: 'FILTERED', label: 'Export filtered list (CSV)', file: 'students-filtered.csv' },
  { value: 'SELECTED', label: 'Export selected (CSV)', file: 'students-selected.csv' },
  { value: 'ALL', label: 'Export all students (CSV)', file: 'students-all.csv' },
];


// ===================================================================== workflow
//
//   website / import / manual  ->  ADMIN (unassigned)  ->  a COUNSELLOR  ->  one of that counsellor's TELECALLERS
//
// A counsellor sees only students assigned to them; a telecaller sees only the students handed to them. The telecaller works
// the call queue: call or WhatsApp, then log an outcome. A final outcome locks the student for the telecaller.

export const isUnassigned = (row: StudentRow): boolean => (row.counsellorName ?? '').trim() === '';

/** Students that came in (website, import) and have no counsellor yet: the admin's to-do list. */
export const unassignedStudents = (rows: StudentRow[]): StudentRow[] => rows.filter(isUnassigned);

const sameName = (a: string | null | undefined, b: string | null | undefined): boolean => (a ?? '').trim().toLowerCase() === (b ?? '').trim().toLowerCase();

export const activeCounsellors = (users: UserRow[]): UserRow[] => users.filter((u) => u.role === 'COUNSELLOR' && u.isActive);

/** The active telecallers who work under the named counsellor. */
export const teamOfCounsellor = (users: UserRow[], counsellorName: string): UserRow[] => {
  const boss = users.find((u) => u.role === 'COUNSELLOR' && sameName(userFullName(u), counsellorName));

  return boss === undefined ? [] : users.filter((u) => u.role === 'TELECALLER' && u.isActive && u.reportsTo === boss.id);
};

/** Who may be chosen as a student's telecaller: only the assigned counsellor's own team. No counsellor means no telecaller. */
export const telecallerChoices = (users: UserRow[], counsellorName: string | null | undefined): string[] =>
  teamOfCounsellor(users, counsellorName ?? '')
    .map(userFullName)
    .sort((a, b) => a.localeCompare(b));

/** The counsellor a telecaller works under (their own name for a counsellor). Used to file a telecaller's new student correctly. */
export const counsellorOfTelecaller = (users: UserRow[], telecallerName: string): string => {
  const caller = users.find((u) => u.role === 'TELECALLER' && sameName(userFullName(u), telecallerName));
  const boss = caller?.reportsTo === undefined || caller.reportsTo === null ? undefined : users.find((u) => u.id === caller.reportsTo);

  return boss === undefined ? '' : userFullName(boss);
};

/** Whether a (counsellor, telecaller) pair is allowed. Empty values are fine; a telecaller needs a counsellor, and must be on their team. */
export const assignmentProblem = (users: UserRow[], counsellorName: string | null | undefined, telecallerName: string | null | undefined): string | null => {
  const counsellor = (counsellorName ?? '').trim();
  const telecaller = (telecallerName ?? '').trim();

  if (counsellor !== '' && !users.some((u) => u.role === 'COUNSELLOR' && u.isActive && sameName(userFullName(u), counsellor))) return `${counsellor} is not an active counsellor.`;
  if (telecaller === '') return null;
  if (counsellor === '') return 'Assign a counsellor first: a telecaller works under one.';
  if (!teamOfCounsellor(users, counsellor).some((u) => sameName(userFullName(u), telecaller))) return `${telecaller} does not work under ${counsellor}.`;

  return null;
};

/** Reassigning a student to a different counsellor drops a telecaller who is not on the new counsellor's team. */
export const assignmentPatch = (users: UserRow[], current: StudentRow, nextCounsellor: string | null): Record<string, unknown> => {
  const patch: Record<string, unknown> = { counsellorName: nextCounsellor };
  const keep = nextCounsellor !== null && telecallerChoices(users, nextCounsellor).some((n) => sameName(n, current.telecallerName));

  if (!keep && (current.telecallerName ?? '').trim() !== '') patch.telecallerName = null;

  return patch;
};

/**
 * Round robin: the id after `lastId` in `ids` (wrapping to the first). With no history, or when the last one chosen is no
 * longer in the list (deactivated, deleted), the rotation starts again from the first. Null for an empty list.
 * The caller keeps `ids` in a stable order and remembers whom it returned last.
 */
export const nextInRotation = (ids: string[], lastId: string | null | undefined): string | null => {
  if (ids.length === 0) return null;

  const at = lastId === null || lastId === undefined ? -1 : ids.indexOf(lastId);

  return ids[(at + 1) % ids.length] ?? null;
};

/** Is this student off-limits to the role right now? A telecaller cannot touch a student whose status is final. */
export const isLockedFor = (role: DeskRole, row: Pick<StudentRow, 'deskStatus'>): boolean => (ROLE_PROFILES[role]?.lockedWhenFinal ?? false) && isFinalStatus(row.deskStatus);

export const LOCKED_MESSAGE = 'This student has a final status, so it is locked. Ask the counsellor to reopen it if it needs another call.';

/** Statuses a person may set. A telecaller closing a lead is allowed (it locks it); there are no others to hide. */
export const statusChoices = (): Array<{ value: string; label: string; final: boolean }> =>
  Object.entries(DESK_STATUS_LABELS).map(([value, label]) => ({ value, label, final: isFinalStatus(value) }));

// ---------------------------------------------------------------- calling links

const tenDigits = (value: string | null | undefined): string => (value ?? '').replace(/\D/g, '').slice(-10);

/** `tel:+91XXXXXXXXXX`, which opens the phone's dialler. Null when the number is not a valid 10-digit mobile. */
export const telUrl = (phone: string | null | undefined): string | null => {
  const digits = tenDigits(phone);

  return digits.length === 10 ? `tel:+91${digits}` : null;
};

/** `https://wa.me/91XXXXXXXXXX?text=...`, which opens WhatsApp (the app on a phone, WhatsApp Web on a computer). */
export const whatsappUrl = (phone: string | null | undefined, message?: string): string | null => {
  const digits = tenDigits(phone);

  if (digits.length !== 10) return null;

  return `https://wa.me/91${digits}${message !== undefined && message.trim() !== '' ? `?text=${encodeURIComponent(message.trim())}` : ''}`;
};

/** A short opening line for WhatsApp, so the message is not blank. The student's own name only: nothing about fees or universities. */
export const whatsappGreeting = (studentName: string | null | undefined, callerName: string): string => {
  const who = (studentName ?? '').trim();

  return `Hello${who === '' ? '' : ` ${who}`}, this is ${callerName.trim() === '' ? 'your counsellor' : callerName.trim()} from eWings Abroad. I tried to reach you about your MBBS abroad enquiry. When is a good time to talk?`;
};

// ------------------------------------------------------------- logging a call

export type CallOutcomeForm = { status: string; nextCallAt: string; followUpStatus: string; note: string };

export const EMPTY_OUTCOME: CallOutcomeForm = { status: '', nextCallAt: '', followUpStatus: '', note: '' };

export const MAX_NOTE = 500;

export type OutcomeErrors = Partial<Record<keyof CallOutcomeForm, string>>;

/** A non-final outcome must say when to call next (and not in the past); a final one needs no date. */
export const validateOutcome = (form: CallOutcomeForm, now: Date = new Date()): OutcomeErrors => {
  const errors: OutcomeErrors = {};

  if (form.status === '' || !(form.status in DESK_STATUS_LABELS)) errors.status = 'Choose the outcome of this call.';

  if (form.status !== '' && !isFinalStatus(form.status)) {
    const when = form.nextCallAt === '' ? NaN : new Date(form.nextCallAt).getTime();

    if (Number.isNaN(when)) errors.nextCallAt = 'Choose when to call next.';
    else if (when < now.getTime() - 5 * 60 * 1000) errors.nextCallAt = 'The next call cannot be in the past.';
  }

  if (form.note.length > MAX_NOTE) errors.note = `Keep the note under ${MAX_NOTE} characters.`;
  if (form.followUpStatus !== '' && !(form.followUpStatus in FOLLOW_UP_LABELS)) errors.followUpStatus = 'Choose a follow-up status from the list.';

  return errors;
};

/** The record changes a logged call makes. A final status clears the next call; anything else sets it. */
export const outcomePatch = (form: CallOutcomeForm): Record<string, unknown> => ({
  deskStatus: form.status,
  followUpStatus: form.followUpStatus === '' ? null : form.followUpStatus,
  nextCallAt: isFinalStatus(form.status) ? null : new Date(form.nextCallAt).toISOString(),
});

/** One line for the student's history, written by the server with the real author and time, not the browser's claim. */
export const callLogLine = (form: CallOutcomeForm): string => {
  const status = DESK_STATUS_LABELS[form.status] ?? form.status;
  const note = form.note.trim();

  return note === '' ? `Call logged: ${status}` : `Call logged: ${status}. ${note}`;
};

// ------------------------------------------------------------- what each role sees first

/** The telecaller's day: today's calls first, then overdue ones, then later ones. Final students never appear. */
export const callList = (rows: StudentRow[], now: Date = new Date()): { today: StudentRow[]; overdue: StudentRow[]; later: StudentRow[] } => ({
  today: inQueue(rows, 'TODAY', now),
  overdue: inQueue(rows, 'PENDING', now),
  later: inQueue(rows, 'UPCOMING', now),
});

/** New students handed to a caller who have no next call yet: they still need a first call. */
export const needsFirstCall = (rows: StudentRow[]): StudentRow[] =>
  rows.filter((row) => !isFinalStatus(row.deskStatus) && row.isBlocked !== true && !row.nextCallAt && (row.telecallerName ?? '').trim() !== '');


/** Students a counsellor has taken on but not yet handed to a telecaller: the counsellor's to-do list. */
export const needsTelecaller = (rows: StudentRow[]): StudentRow[] =>
  rows.filter((row) => (row.counsellorName ?? '').trim() !== '' && (row.telecallerName ?? '').trim() === '' && !isFinalStatus(row.deskStatus) && row.isBlocked !== true);

/** The call queue as sections for the telecaller's screen, each already sorted, with the first-call list added. */
export const callSections = (rows: StudentRow[], now: Date = new Date()): Array<{ id: 'FIRST' | 'OVERDUE' | 'TODAY' | 'LATER'; title: string; rows: StudentRow[] }> => {
  const list = callList(rows, now);

  return [
    { id: 'OVERDUE' as const, title: 'Overdue', rows: list.overdue },
    { id: 'TODAY' as const, title: 'Today', rows: list.today },
    { id: 'FIRST' as const, title: 'New: first call', rows: needsFirstCall(rows) },
    { id: 'LATER' as const, title: 'Later', rows: list.later },
  ];
};

/** One word for how late a call is, for the queue cards. */
export const dueLabel = (nextCallAt: string | null | undefined, now: Date = new Date()): string => {
  if (!nextCallAt) return 'No call scheduled';

  const due = new Date(nextCallAt);

  if (Number.isNaN(due.getTime())) return 'No call scheduled';

  const minutes = Math.round((due.getTime() - now.getTime()) / 60_000);

  if (minutes < -60 * 24) return `${Math.round(-minutes / (60 * 24))} day${Math.round(-minutes / (60 * 24)) === 1 ? '' : 's'} overdue`;
  if (minutes < -60) return `${Math.round(-minutes / 60)} h overdue`;
  if (minutes < 0) return `${-minutes} min overdue`;

  return formatCallDate(nextCallAt);
};
